import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CheckCircle2, CreditCard, RefreshCw, Search, ShieldCheck, UserCheck, Users, XCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageShell } from "@/pages/dashboard/RolePages";

const ROLE_LABELS = {
  user: "Usuario",
  student: "Estudiante",
  consultant: "Consultor",
  creator: "Creador",
  admin: "Administrador",
  support: "Soporte",
};

const sourceLabel = {
  individual_allow: "Autorizado por ID KD9",
  individual_deny: "Bloqueado por ID KD9",
  role: "Autorizado por rol",
  disabled: "No autorizado",
  director_excluded: "Director excluido",
};

export default function ThirdPartyPayments() {
  const [access, setAccess] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setError("");
    try { setAccess((await api.get("/api/third-party-payments/access")).data); }
    catch (err) { setError(formatApiError(err)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return <PageShell
    kicker="OnePay · Pago a terceros"
    title="PAGO A TERCEROS"
    desc="Acceso controlado por Dirección KD9. Este panel queda listo para conectar la dispersión real con Wompi en la siguiente fase."
    badge={<button onClick={load} className="kd9-btn-outline rounded-lg p-2" aria-label="Actualizar"><RefreshCw size={15}/></button>}
  >
    {error && <div className="kd9-card mb-5 p-4 text-sm text-red-300">{error}</div>}
    {!access ? <div className="kd9-card p-8 text-center">Verificando autorización…</div> :
      <div className="grid gap-5 lg:grid-cols-3">
        <section className="kd9-card p-6 lg:col-span-2">
          <div className="flex items-center gap-3">
            {access.enabled ? <CheckCircle2 className="text-emerald-300" /> : <XCircle className="text-amber-300" />}
            <div>
              <div className="font-cinzel text-xl">{access.enabled ? "ACCESO AUTORIZADO" : "ACCESO NO AUTORIZADO"}</div>
              <div className="mt-1 text-sm opacity-60">{sourceLabel[access.source] || access.source}</div>
            </div>
          </div>
          {access.enabled ? <>
            <div className="mt-6 rounded-xl border border-white/10 p-5">
              <div className="flex items-center gap-2 font-cinzel kd9-gold-text"><CreditCard size={18}/> Canal de dispersión</div>
              <p className="mt-2 text-sm opacity-70">Gobierno de permisos activo. La salida real de dinero permanecerá bloqueada hasta conectar y validar Wompi Pagos a Terceros.</p>
              <div className="mt-4 rounded-lg border border-amber-400/20 bg-amber-400/5 p-3 text-xs">
                Estado técnico: <strong>Panel listo · integración Wompi pendiente</strong>
              </div>
            </div>
          </> : <p className="mt-5 text-sm opacity-70">Dirección puede habilitar este acceso por rol completo o específicamente para tu ID KD9.</p>}
        </section>
        <section className="kd9-card p-6">
          <div className="font-cinzel kd9-gold-text">SEGURIDAD</div>
          <div className="mt-4 space-y-3 text-sm opacity-75">
            <p>• El permiso es independiente del rol principal.</p>
            <p>• Una excepción por ID KD9 tiene prioridad sobre el rol.</p>
            <p>• Dirección puede activar, bloquear o devolver al comportamiento del rol.</p>
            <p>• No se ejecutan pagos reales hasta integrar Wompi.</p>
          </div>
        </section>
      </div>
    }
  </PageShell>;
}

export function ThirdPartyPaymentsGovernance() {
  const [data, setData] = useState({ roles: {}, overrides: [] });
  const [search, setSearch] = useState("");
  const [users, setUsers] = useState([]);
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try { setData((await api.get("/api/director/third-party-payments/governance")).data); }
    catch (err) { setNotice(formatApiError(err)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const roleRows = useMemo(() => Object.keys(ROLE_LABELS).map(role => ({
    role,
    label: ROLE_LABELS[role],
    enabled: Boolean(data.roles?.[role]),
  })), [data.roles]);

  const setRole = async (role, enabled) => {
    setBusy("role:"+role); setNotice("");
    try {
      await api.put("/api/director/third-party-payments/roles/"+role, { enabled });
      setNotice(`${ROLE_LABELS[role]}: Pago a terceros ${enabled ? "habilitado" : "deshabilitado"} por rol.`);
      await load();
    } catch (err) { setNotice(formatApiError(err)); }
    finally { setBusy(""); }
  };

  const findUsers = async (event) => {
    event?.preventDefault();
    if (search.trim().length < 2) return;
    setBusy("search"); setNotice("");
    try { setUsers((await api.get("/api/director/third-party-payments/users", { params: { search: search.trim() } })).data); }
    catch (err) { setNotice(formatApiError(err)); }
    finally { setBusy(""); }
  };

  const setUser = async (user, enabled) => {
    setBusy("user:"+user.user_id); setNotice("");
    try {
      await api.put("/api/director/third-party-payments/users/"+user.user_id, { enabled });
      setNotice(`${user.kd9_id || user.email}: ${enabled ? "autorizado" : "bloqueado"} individualmente.`);
      await Promise.all([load(), findUsers()]);
    } catch (err) { setNotice(formatApiError(err)); }
    finally { setBusy(""); }
  };

  const inheritUser = async (user) => {
    setBusy("user:"+user.user_id); setNotice("");
    try {
      await api.delete("/api/director/third-party-payments/users/"+user.user_id);
      setNotice(`${user.kd9_id || user.email}: vuelve a heredar la regla de su rol.`);
      await Promise.all([load(), findUsers()]);
    } catch (err) { setNotice(formatApiError(err)); }
    finally { setBusy(""); }
  };

  return <PageShell
    kicker="Dirección · Gobierno OnePay"
    title="GOBIERNO DE PAGO A TERCEROS"
    desc="Controla quién podrá usar Pago a Terceros. Puedes habilitar un rol completo y luego crear excepciones individuales por ID KD9."
    badge={<button onClick={load} className="kd9-btn-outline rounded-lg p-2" aria-label="Actualizar"><RefreshCw size={15}/></button>}
  >
    {notice && <div className="kd9-card mb-5 p-4 text-sm">{notice}</div>}

    <section className="kd9-card p-5">
      <div className="flex items-start gap-3">
        <ShieldCheck className="kd9-gold-text" />
        <div>
          <h2 className="font-cinzel text-xl">Acceso por rol</h2>
          <p className="mt-1 text-sm opacity-60">El Director no usa este canal. Estos interruptores definen la regla base para los demás roles.</p>
        </div>
      </div>
      <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {roleRows.map(row => <div key={row.role} className="rounded-xl border border-white/10 p-4">
          <div className="flex items-center justify-between gap-3">
            <div><div className="font-cinzel">{row.label}</div><div className="mt-1 text-xs opacity-50">{row.role}</div></div>
            <button
              disabled={busy==="role:"+row.role}
              onClick={()=>setRole(row.role,!row.enabled)}
              className={row.enabled ? "kd9-btn-primary rounded-lg px-4 py-2 text-xs" : "kd9-btn-outline rounded-lg px-4 py-2 text-xs"}
            >{row.enabled ? "Habilitado" : "Deshabilitado"}</button>
          </div>
        </div>)}
      </div>
    </section>

    <section className="kd9-card mt-5 p-5">
      <div className="flex items-start gap-3">
        <Users className="kd9-gold-text" />
        <div><h2 className="font-cinzel text-xl">Excepción por ID KD9</h2><p className="mt-1 text-sm opacity-60">Busca por ID KD9, nombre o correo. Una decisión individual prevalece sobre la regla del rol.</p></div>
      </div>
      <form onSubmit={findUsers} className="mt-4 flex flex-col gap-2 sm:flex-row">
        <input className="kd9-input flex-1 p-3" value={search} onChange={e=>setSearch(e.target.value)} placeholder="Ej. KD9-..., nombre o correo"/>
        <button className="kd9-btn-primary rounded-lg px-5 py-3" disabled={busy==="search" || search.trim().length<2}><Search size={15} className="mr-2 inline"/>{busy==="search"?"Buscando…":"Buscar"}</button>
      </form>

      <div className="mt-5 grid gap-3">
        {users.map(user => <article key={user.user_id} className="rounded-xl border border-white/10 p-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="font-cinzel text-lg">{[user.name,user.surname].filter(Boolean).join(" ") || user.email}</div>
              <div className="mt-1 font-mono text-xs kd9-gold-text">{user.kd9_id || "Sin ID KD9"}</div>
              <div className="mt-1 text-xs opacity-55">{user.email} · {(user.roles||[]).map(r=>ROLE_LABELS[r]||r).join(" · ")}</div>
              <div className="mt-2 text-xs">Estado de acceso: <strong>{sourceLabel[user.access?.source] || "—"}</strong></div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button disabled={busy==="user:"+user.user_id} onClick={()=>setUser(user,true)} className="kd9-btn-primary rounded-lg px-3 py-2 text-xs"><UserCheck size={13} className="mr-1 inline"/>Autorizar</button>
              <button disabled={busy==="user:"+user.user_id} onClick={()=>setUser(user,false)} className="kd9-btn-outline rounded-lg px-3 py-2 text-xs">Bloquear</button>
              <button disabled={busy==="user:"+user.user_id} onClick={()=>inheritUser(user)} className="kd9-btn-outline rounded-lg px-3 py-2 text-xs">Heredar rol</button>
            </div>
          </div>
        </article>)}
        {search && !users.length && busy!=="search" && <div className="rounded-xl border border-white/10 p-5 text-center text-sm opacity-60">Sin resultados para esta búsqueda.</div>}
      </div>
    </section>

    <section className="kd9-card mt-5 p-5">
      <h2 className="font-cinzel text-xl">Excepciones activas</h2>
      <div className="mt-4 grid gap-2">{(data.overrides||[]).map(row=><div key={row.user_id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-white/10 p-3 text-sm">
        <div><strong>{row.kd9_id || row.email || row.user_id}</strong><div className="text-xs opacity-50">{row.name || "Usuario"} · {(row.roles||[]).join(", ")}</div></div>
        <span className={row.enabled ? "text-emerald-300" : "text-amber-300"}>{row.enabled ? "Autorizado" : "Bloqueado"}</span>
      </div>)}</div>
      {!data.overrides?.length && <p className="mt-3 text-sm opacity-55">No hay excepciones individuales. Todos heredan la regla de su rol.</p>}
    </section>
  </PageShell>;
}
